// React and MobX
import React, { useState } from "react";
import { useObserver } from "mobx-react-lite";

// Material-UI Core and Styles
import { Accordion, AccordionSummary, Box, CardMedia, Drawer, IconButton, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import CheckIcon from "@material-ui/icons/Check";
import ClearIcon from "@material-ui/icons/Clear";
import { Close as CloseIcon } from "@material-ui/icons";

// Core
import { Loader } from "Core/Components/Loader";

// Custom Components
import { NavButton } from "Custom/Components/Buttons/NavButton";
import { Error } from "Custom/Components/Error";
import { PrivateBlob } from "Custom/Components/Image/PrivateImage";
import { Stores, StoresContext } from "Custom/Stores";

// Custom Styles
import { DashboardPageStyles, usePageAnimationStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";
import { ChoicesAreaPageStyles } from "Custom/StylesAppSpecific/DashboardSubViews/ChoiceAreaPageStyling";

// Custom ViewModels
import { ChoicesAreaViewModel } from "Custom/ViewModels/DashboardSubViewModels/ChoicesAreaViewModel";

// Enums and Models
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";

// Custom SVG Components
import { ReactComponent as CircleRightArrow } from "../../Content/substraction.svg";
import { ReactComponent as SquareLeftArrow } from "../../Content/play-backward-icon.svg";
import ChoicesProductGroup from "./ChoicesProductGroup";

interface ChoicesAreaProps {
    propertyId: string;
    areaId: string;
    onClose?: () => void;
    stores: Stores;
}

export const ChoicesArea: React.FC<ChoicesAreaProps> = ({ propertyId, areaId, onClose, stores }) => {
    // #region Code Behind
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;

    const dashboardPageStyles = DashboardPageStyles();
    const choicesAreaPageStyles = ChoicesAreaPageStyles();
    const pageAnimationStyles = usePageAnimationStyles();
    const portalThemeStore = stores.domain.PortalThemeStore;

    const [viewModel] = useState(() => new ChoicesAreaViewModel(propertyId, areaId));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedProductGroupId, setSelectedProductGroupId] = useState<string | null>(null);

    const handleOpenDrawer = (productGroupId: string) => {
        setSelectedProductGroupId(productGroupId);
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setSelectedProductGroupId(null);
    };

    const onChoicesAreaProductGroupClick = (areaId: string, productGroupId: string) => {
        if (isDesktop) {
            viewModel.navigateToProductGroup(propertyId, areaId, productGroupId); // Navigate for desktop
        } else {
            setSelectedProductGroupId(productGroupId);
            setDrawerOpen(true);
        }
    };

    // #endregion Code Behind

    const renderBusy = () => <Loader loadingText={viewModel.apiStatusLoadingMessage} />;

    const renderError = () => <Error errorText={viewModel.apiStatusErrorMessage} />;

    const renderSummaryText = () => {
        if (viewModel.isChoicesLocked) {
            return <Typography>Choices are disabled. You can view but you can't add or remove any. If you wish to make changes, please contact your sales agent.</Typography>;
        } else {
            return (
                <Typography>Select an option then choose to upgrade and add any extras. Once you have finished add them to your order via the "add to order" button.</Typography>
            );
        }
    };

    const renderBackToChoices = () =>
        isDesktop ? (
            <Box
                className={choicesAreaPageStyles.backTochoices}
                style={{
                    ...(portalThemeStore.secondaryColour ? { backgroundColor: portalThemeStore.secondaryColour } : {}),
                }}
            >
                <SquareLeftArrow
                    className="arrowIcon"
                    onClick={() => viewModel.navigateToChoices(propertyId)}
                    style={{ cursor: "pointer", ...(portalThemeStore.primaryColour ? { color: portalThemeStore.primaryColour } : {}) }}
                />
                <NavButton displayName="Back to choices" />
            </Box>
        ) : null;

    const renderTitleRow = () => (
        <Box
            className={choicesAreaPageStyles.titleRowHeader}
            style={{
                ...(portalThemeStore.primaryColour ? { backgroundColor: portalThemeStore.primaryColour } : {}),
                ...(portalThemeStore.primaryColour ? { borderBottomColor: portalThemeStore.secondaryColour } : {}),
            }}
        >
            <Box className={choicesAreaPageStyles.titleImageRow}>
                <PrivateBlob src={viewModel.areaImage} title="" cardMedia={true} />
                {!isDesktop && onClose && (
                    <IconButton onClick={onClose} className="closeBtn">
                        <CloseIcon />
                    </IconButton>
                )}
            </Box>
            <Box className={choicesAreaPageStyles.titleTextRow}>
                <Typography variant={"h5"}>{viewModel.model.areaName}</Typography>
                {renderSummaryText()}
            </Box>
        </Box>
    );

    const renderForms = () => (
        <Box className={choicesAreaPageStyles.customAccordion}>
            {viewModel.productGroups.map((productGroup, index) => (
                <Accordion className={choicesAreaPageStyles.accordion} key={productGroup.model.id} TransitionProps={{ unmountOnExit: true, timeout: 0 }}>
                    <AccordionSummary
                        className={choicesAreaPageStyles.accordionSummary}
                        aria-label="Navigate"
                        aria-controls={`additional-content${index}`}
                        id={`additional-header${index}`}
                        onClick={() => onChoicesAreaProductGroupClick(areaId, productGroup.model.id)}
                    >
                        <Box className={choicesAreaPageStyles.productGroupTitleContainer}>
                            <Typography>{productGroup.model.productGroupName}</Typography>
                            <Box className={choicesAreaPageStyles.productGroupCounterContainer}>
                                <Box className={choicesAreaPageStyles.productGroupCounterContainerListRow}>
                                    <Typography component="p">{productGroup.numberOfExtras}</Typography>
                                    <Typography component="p">extras added</Typography>
                                </Box>
                                <Box className={choicesAreaPageStyles.productGroupCounterContainerListRow}>
                                    <Typography component="p">{productGroup.isChoiceSelected ? <CheckIcon /> : <ClearIcon />}</Typography>
                                    <Typography component="p">option selected</Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box className={choicesAreaPageStyles.navigateToArrow}>
                            <CircleRightArrow />
                        </Box>
                    </AccordionSummary>
                </Accordion>
            ))}
        </Box>
    );

    return useObserver(() => (
        <Box className={`${choicesAreaPageStyles.root}  ${pageAnimationStyles.pageWrapper} mobileDrawerHeight`}>
            <Box className={` ${dashboardPageStyles.workspace}  `}>
                {viewModel.ApiStatus === ApiStatus.success && (
                    <React.Fragment>
                        {renderBackToChoices()}
                        {renderTitleRow()}
                        {renderForms()}
                    </React.Fragment>
                )}
                {viewModel.ApiStatus === ApiStatus.loading && <React.Fragment>{renderBusy()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.error && <React.Fragment>{renderError()}</React.Fragment>}
            </Box>

            {/* Drawer for Mobile */}
            <Drawer anchor="bottom" open={drawerOpen} onClose={handleCloseDrawer} className="choicesPageMobileDrawerSecondModal">
                {selectedProductGroupId && (
                    <ChoicesProductGroup propertyId={propertyId} areaId={areaId} productGroupId={selectedProductGroupId} onClose={handleCloseDrawer} stores={stores} />
                )}
            </Drawer>
        </Box>
    ));
};
